/**
 * Visibility presets for canvas elements.
 *
 * ARCHITECTURE (v5.3):
 *   `_visibility` controls OPACITY ONLY, never presence on the canvas.
 *   Valid values are in the open interval (0, 1]:
 *     - VISIBLE   (1.0)  — primary trace, standard edges, the focus of attention
 *     - SECONDARY (0.8)  — supporting context: BFS-explored secondary-trace edges
 *                          (both endpoints reached but not on the chosen path)
 *     - DIMMED    (0.55) — transient hint (SSE discovering, Follow Money context)
 *     - PRUNED    (0.15) — BFS-deprioritized / ambient-noise edges; cleared
 *                          when the orchestrator emits `complete`
 *     - EARLIER_RUN (0.28) — Case Canvas: a stale edge or a dormant node, seen
 *                          only in earlier case-worker runs (FIX38, RET01)
 *
 *   "Hidden" means STASHED, never a `_visibility` of 0. To take an element off
 *   the canvas call `canvasController.stash(ids, reason)` (restore with
 *   `restore` / `restoreByReason`). Writing 0.0 is rejected by the controller,
 *   and there is deliberately no HIDDEN preset in this list.
 *
 *   This file is the single list of opacity presets. No stylesheet class may set
 *   `opacity` (importance=low, edge-deemphasized, edge-unfocused, hover-deemphasis,
 *   the AMBIENT intents and node.dimmed all went through here in WB12).
 *   Short-lived interaction dims (hover, edge focus, de-emphasis) use
 *   `utils/transientVisibility.ts`, which restores the previous value.
 *
 *   New levels MUST be added here, not inlined as raw numbers — see R11
 *   in CLAUDE.md. A snapshot audit on May 5 found 10 elements stuck at
 *   an undocumented 0.3 (discovering edges that never got reset post-SSE),
 *   including the gold-path trace anchor. Both fixed by collapsing to
 *   DIMMED and resetting to VISIBLE on stream complete.
 */
export const VISIBILITY = {
    /** Fully visible — primary trace, standard edges */
    VISIBLE: 1.0,
    /** Supporting context — BFS-explored / secondary-trace edges */
    SECONDARY: 0.8,
    /** Semi-transparent but still interactive (SSE discovering, Follow Money context) */
    DIMMED: 0.55,
    /** BFS-deprioritized / ambient noise; cleared when orchestrator emits `complete` */
    PRUNED: 0.15,
    /**
     * Case Canvas: seen only in earlier case-worker runs. A stale edge (FIX38) and a dormant
     * node (RET01: every edge an earlier run's) are drawn at this level; the data says why.
     */
    EARLIER_RUN: 0.28,
} as const;

export type VisibilityLevel = typeof VISIBILITY[keyof typeof VISIBILITY];
